// import Icon from "@/components/Icon";
import {
  Box,
  ChakraProps,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  Flex,
  Icon,
  Grid,
  GridItem,
  Center,
  chakra,
} from "@chakra-ui/react";
import { ReactNode } from "react";
import { Check, Close, TooltipInfo } from "../../../components/Icons";

interface Props {
  isOpen: boolean;
  onClose(): void;
  title: string | ReactNode;
  content: ReactNode;
  headerAffix?: ReactNode;
  tableData?: TableData;
}

const goldGradient = `linear-gradient(90deg, #E6A441, #FAD998);`;

const titleStyle: ChakraProps = {
  fontSize: "24px",
  pt: "48px",
  pb: "32px",
  textAlign: "center",
  fontWeight: 600,
};

export interface TableItemProps {
  primaryKeyStyle?: ChakraProps;
  items: Array<ReactNode | string>;
}

export interface TableProps {
  title?: string;
  rows: TableItemProps[];
}

export interface TableData {
  header: TableItemProps;
  contents: TableProps[];
}

export const TableItem = chakra((props: TableItemProps) => {
  const { primaryKeyStyle = {}, items, ...other } = props;
  return (
    <Grid templateColumns="1.5fr 1fr 1fr" color="white" h="36px" px="32px" {...other}>
      {items.map((item, idx) => {
        const style = idx === 0 ? primaryKeyStyle : {};
        return (
          <GridItem key={idx} {...style}>
            <Flex
              h="100%"
              w="100%"
              align="center"
              justify={idx === 0 ? "flex-start" : "center"}
              userSelect="none"
            >
              {item}
            </Flex>
          </GridItem>
        );
      })}
    </Grid>
  );
});

export const Table = chakra((props: TableProps) => {
  const { rows, title, ...other } = props;
  return (
    <Box color="white" {...other}>
      {title && <Text>{title}</Text>}
      <Box bgColor="#323232" rounded="6px" py="6px">
        {rows.map((row, idx) => {
          return <TableItem key={idx} {...row} />;
        })}
      </Box>
    </Box>
  );
});

const defaultTableData: TableData = {
  header: {
    items: ["会员权益对比", "非会员用户", "会员用户"],
    primaryKeyStyle: { color: "#999999" },
  },
  contents: [
    {
      title: "画宇宙功能",
      rows: [
        {
          items: [
            <TooltipInfo
              key={`i1`}
              title="核心功能"
              info="包括：使用画宇宙系列模型生成、相似、垫图、替换、去除、外延，以及高清、抠图等功能"
            />,
            <Check boxSize="16px" key={`核心功能${1}`} />,
            <Check boxSize="16px" key={`核心功能${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo key={`i2`} title="使用小模型" info="小模型属于画宇宙系列模型" />,
            <Check boxSize="16px" key={`使用小模型${1}`} />,
            <Check boxSize="16px" key={`使用小模型${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo
              key={`i3`}
              title="不限量使用"
              info="非会员用户每日登录可获赠 30 免费画力星（仅可用于画宇宙系列模型），生成一张 512*512 图消耗 2 画力星，尺寸越大消耗越多；会员用户可不限量使用核心功能"
            />,
            <Close boxSize="16px" key={`不限量使用${1}`} />,
            <Check boxSize="16px" key={`不限量使用${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo
              key={`i4`}
              title="算力加速"
              info="非会员用户使用画宇宙功能将进入普通算力通道；会员用户使用算力加速通道，排队人数少，等待时间短"
            />,
            <Close boxSize="16px" key={`算力加速${1}`} />,
            <Check boxSize="16px" key={`算力加速${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo
              key={`i5`}
              title="不限制提交任务数"
              info="非会员用户提交画宇宙核心功能的同类任务只能提交 1 个；会员用户无此限制"
            />,
            <Close boxSize="16px" key={`不限制提交任务数${1}`} />,
            <Check boxSize="16px" key={`不限制提交任务数${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo
              key={`i6`}
              title="批量生成多张"
              info="非会员用户使用画宇宙功能，一次只能生成 1 张图；会员用户最多一次批量生成 100 张图"
            />,
            <Close boxSize="16px" key={`批量生成多张${1}`} />,
            <Check boxSize="16px" key={`批量生成多张${2}`} />,
          ],
        },
      ],
    },
    {
      title: "第三方模型（单独消耗画力星）",
      rows: [
        {
          items: [
            <TooltipInfo
              key={`i7`}
              title="Draft 动漫模型"
              info="由「draft.art」提供，生成一张 512*512 图消耗 5 画力星，尺寸越大，消耗越多"
            />,
            <Check boxSize="16px" key={`Draft 动漫模型${1}`} />,
            <Check boxSize="16px" key={`Draft 动漫模型${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo
              key={`i8`}
              title="百度文心模型"
              info="由「百度文心 ERNIE-ViLG」提供，生成一张 1024*1024 图消耗 10 画力星"
            />,
            <Check boxSize="16px" key={`百度文心模型${1}`} />,
            <Check boxSize="16px" key={`百度文心模型${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo
              key={`i9`}
              title="社区大模型"
              info="由「猴子无限」提供，生成一张 512*512 图消耗 3 画力星"
            />,
            <Check boxSize="16px" key={`社区大模型${1}`} />,
            <Check boxSize="16px" key={`社区大模型${2}`} />,
          ],
        },
        {
          items: [
            <TooltipInfo
              key={`i10`}
              title="画力星优惠价"
              info="非会员用户购买画力星均价约 0.025 元，会员用户购买画力星单价为 0.015 元（6 折优惠）"
            />,
            <Close boxSize="16px" key={`画力星优惠价${1}`} />,
            <Check boxSize="16px" key={`画力星优惠价${2}`} />,
          ],
        },
      ],
    },
  ],
};

export default function PayModalWithTable(props: Props) {
  const { isOpen, onClose, title, content, headerAffix, tableData = defaultTableData } = props;
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg="transparent" alignItems="center" mx="auto" shadow="none" w="auto">
        <Flex maxW="980px" h="600px">
          <Box
            pos="relative"
            w="500px"
            h="600px"
            rounded="20px"
            bgColor="#323232"
            overflow="hidden"
          >
            <ModalCloseButton
              color="white"
              fontSize="14px"
              w="14px"
              h="14px"
              top="25px"
              right="25px"
            />
            {headerAffix ? (
              <Box pos="absolute" left={0} top={0}>
                {headerAffix}
              </Box>
            ) : null}
            <Flex w="500px">
              {/* right */}
              <Box h="100%" w="500px" px="24px">
                {typeof title === "string" ? (
                  <Text {...titleStyle} bg={goldGradient} bgClip="text">
                    {title}
                  </Text>
                ) : (
                  title
                )}
                {content}
                {/* <Text align="right" color="#999999" fontSize="10px" lineHeight="22px" mt="8px">
                  虚拟商品，不支持退款
                </Text> */}
              </Box>
            </Flex>
          </Box>
          {tableData ? (
            <Box h="100%" w="480px" py="24px">
              <Box bg="#2d2d2d" w="100%" h="100%" borderRightRadius="20px" px="22px" py="28px">
                <TableItem {...tableData.header} bg="#323232" rounded="6px" />
                {tableData.contents.map((content, idx) => (
                  <Table key={idx} {...content} mt="12px" />
                ))}
              </Box>
            </Box>
          ) : null}
        </Flex>
      </ModalContent>
    </Modal>
  );
}
